/**
 * Tab Panels Block Script
 *
 * ARIA tabs pattern: clicking or arrow-keying through .rmh-tab-panels__tablist
 * activates one tab at a time. Each tab's aria-controls points at its panel;
 * non-active panels carry `hidden` + `inert`.
 *
 * Keyboard model follows the WAI-ARIA "automatic activation" pattern:
 *   ArrowLeft / ArrowRight  — move focus and activate
 *   Home / End              — first / last tab
 *
 * This file is compiled by Vite with ES6+ support.
 * CSS is compiled separately - do not import it here.
 */

import { animate } from 'motion';

const FADE_DURATION = 0.25;

/**
 * Read the active/inactive class strings from the tab's data attributes
 * (set by render.php). We carry the strings on each button so the PHP layer
 * stays the single source of truth for variant-specific styling.
 */
function classListFor(tab, kind) {
	const raw = tab.dataset[kind === 'active' ? 'rmhTabActiveClass' : 'rmhTabInactiveClass'] || '';
	return raw.split(/\s+/).filter(Boolean);
}

/**
 * Apply a tab's active/inactive class state without losing classes set by
 * other code (e.g. utility classes Tailwind injects). We diff between the
 * two known class lists rather than wiping the whole list.
 */
function setTabState(tab, isActive) {
	const activeList = classListFor(tab, 'active');
	const inactiveList = classListFor(tab, 'inactive');

	// Remove the OPPOSITE state's classes, then add this state's classes.
	const toRemove = isActive ? inactiveList : activeList;
	const toAdd = isActive ? activeList : inactiveList;

	tab.classList.remove(...toRemove);
	tab.classList.add(...toAdd);

	tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
	tab.setAttribute('tabindex', isActive ? '0' : '-1');
}

function activate(tabs, panels, nextIndex, { focus = false, animate: shouldAnimate = true } = {}) {
	tabs.forEach((tab, i) => {
		setTabState(tab, i === nextIndex);
	});

	panels.forEach((panel, i) => {
		const isNext = i === nextIndex;

		if (isNext) {
			// Reveal first so we can animate from 0 → 1.
			panel.removeAttribute('hidden');
			panel.removeAttribute('inert');

			if (shouldAnimate) {
				panel.style.opacity = '0';
				animate(panel, { opacity: 1 }, { duration: FADE_DURATION, ease: 'easeOut' }).then(() => {
					panel.style.opacity = '';
				});
			}
		} else {
			panel.setAttribute('hidden', '');
			panel.setAttribute('inert', '');
			panel.style.opacity = '';
		}
	});

	if (focus) {
		tabs[nextIndex]?.focus();
	}
}

function handleKeydown(event, tabs, panels, currentIndex) {
	let nextIndex = null;

	switch (event.key) {
		case 'ArrowLeft':
			nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
			break;
		case 'ArrowRight':
			nextIndex = (currentIndex + 1) % tabs.length;
			break;
		case 'Home':
			nextIndex = 0;
			break;
		case 'End':
			nextIndex = tabs.length - 1;
			break;
		default:
			return;
	}

	event.preventDefault();
	activate(tabs, panels, nextIndex, { focus: true });
}

document.addEventListener('DOMContentLoaded', () => {
	const blocks = document.querySelectorAll('.rmh-tab-panels');

	blocks.forEach((block) => {
		const tabs = Array.from(block.querySelectorAll('[role="tab"]'));
		const panels = Array.from(block.querySelectorAll('[role="tabpanel"]'));

		if (tabs.length === 0 || tabs.length !== panels.length) {
			return;
		}

		tabs.forEach((tab, index) => {
			tab.addEventListener('click', () => {
				if (tab.getAttribute('aria-selected') === 'true') {
					return;
				}
				activate(tabs, panels, index);
			});

			tab.addEventListener('keydown', (event) => {
				handleKeydown(event, tabs, panels, index);
			});
		});
	});
});
